import React, { useState, useRef, useEffect } from 'react'
import { useDataEngine, useDataQuery, useDataMutation } from '@dhis2/app-runtime'
import {
    CircularLoader,
    NoticeBox,
    InputField,
    Button,
    TabBar,
    Tab,
    AlertBar
} from '@dhis2/ui'

import StaffList from './components/StaffList'
import StaffForm from './components/StaffForm'
import PositionList from './components/PositionList'
import { deleteStaff, updateStaffComplete } from './api/staffQueries'
import { updateStaff } from '../../utils/api'
import { getAuthHeader } from '../../utils/auth'
import StaffPositionEnforcement from './components/StaffPositionEnforcement'
import BulkStaffImportDialog from './components/BulkStaffImportDialog'; 

const BASE_URL = process.env.REACT_APP_DHIS2_BASE_URL || '';


const query = {
    users: {
        resource: 'users',
        params: {
            fields: 'id,username,firstName,surname,email,userCredentials[username,userRoles[id,name]],organisationUnits[id,name]',
            paging: 1000
        }
    },
    me: {
        resource: 'me',
        params: {
            fields: 'organisationUnits[id,name]'
        }
    }
}

const StaffPage = () => {
    const engine = useDataEngine()
    const [search, setSearch] = useState('')
    const [formVisible, setFormVisible] = useState(false)
    const [selectedStaff, setSelectedStaff] = useState(null)
    const [mode, setMode] = useState('create');
    const [activeTab, setActiveTab] = useState('staff');
    const [deleteMutate] = useDataMutation(deleteStaff)
    const [updateMutate] = useDataMutation(updateStaff)
    const [notification, setNotification] = useState({
        visible: false,
        type: '',
        title: '',
        details: ''
    });

    const [showBulkImport, setShowBulkImport] = useState(false);
    
    const notificationTimeout = useRef();

    const { loading, error, data, refetch } = useDataQuery(query);

    const currentUserOrgUnit = data?.me?.organisationUnits?.[0];
    const currentUserOrgUnitId = currentUserOrgUnit?.id;
    
    // MOVE additionalQueries INSIDE the component so it can access currentUserOrgUnitId
    const additionalQueries = {
        orgUnits: {
          resource: 'organisationUnits',
          params: {
            paging: 1000,
            fields: 'id,name,level,parent[id,name]'
          }
        },
        positions: currentUserOrgUnitId ? {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E', // Position TEI type
            ou: currentUserOrgUnitId, // Use currentUserOrgUnitId, not currentUserOrgUnit
            ouMode: 'DESCENDANTS', // Include sub-units
            paging: 1000,
            fields: 'trackedEntityInstance,attributes[attribute,value],orgUnit'
          }
        } : null
    };
        
    // Add additional data query
    const { 
        loading: additionalLoading, 
        error: additionalError, 
        data: additionalData 
      } = useDataQuery(additionalQueries, {
        lazy: !currentUserOrgUnitId // Only run when we have org unit ID
    });

    // State for staff members from TEI
    const [staffMembers, setStaffMembers] = useState([]);
    const [loadingStaff, setLoadingStaff] = useState(false);
    const [staffError, setStaffError] = useState(null);

    // State to track which tabs have been initialized
    const [initializedTabs, setInitializedTabs] = useState({
        staff: true, // Staff tab is always initialized first
        positions: false,
        assignments: false
    });

    // Derived data for BulkStaffImportDialog
    const departments = additionalData?.orgUnits?.organisationUnits || [];
    const positionsList = additionalData?.positions?.trackedEntityInstances || [];
    
    // Create existingUsers from staffMembers
    const existingUsers = staffMembers.map(staff => ({
        id: staff.id,
        username: staff.userCredentials?.username,
        email: staff.email,
        firstName: staff.firstName,
        surname: staff.surname,
        phoneNumber: staff.phoneNumber
    }));

    // Fetch staff members from TEI
    const fetchStaffMembers = async () => {
        try {
            setLoadingStaff(true);
            setStaffError(null);
            
            // 1. Get current user's org unit
            const { me } = await engine.query({
                me: {
                    resource: 'me',
                    params: { 
                        fields: 'organisationUnits[id,name]' 
                    }
                }
            });

            if (!me.organisationUnits?.length) {
                throw new Error('No organization units assigned to current user');
            }

            const primaryOrgUnit = me.organisationUnits[0];

            // 2. Fetch all users in the system
            const usersResponse = await engine.query({
                users: {
                    resource: 'users',
                    params: {
                        fields: 'id,firstName,surname,email,userCredentials[username]',
                        pageSize: 1000
                    }
                }
            });

            // 3. Fetch all TEIs in the org unit
            const teisResponse = await engine.query({
                teis: {
                    resource: 'trackedEntityInstances',
                    params: {
                        trackedEntityType: 'OaAbpIs1cV7',
                        fields: 'trackedEntityInstance,attributes[attribute,value]',
                        ou: primaryOrgUnit.id,
                        ouMode: 'DESCENDANTS',
                        pageSize: 1000
                    }
                }
            });

            // 4. Match TEIs to users by username
            const matchedStaff = teisResponse.teis.trackedEntityInstances
                .map(tei => {
                    const usernameAttr = tei.attributes.find(attr => 
                        attr.attribute === 'g3RRIfDiQra'
                    );
                    if (!usernameAttr?.value) return null;
                    
                    const user = usersResponse.users.users.find(u => 
                        u.userCredentials?.username === usernameAttr.value
                    );
                    
                    return user ? { ...user, tei } : null;
                })
                .filter(Boolean)
                // Group TEIs by user ID
                .reduce((acc, item) => {
                    if (!acc[item.id]) {
                        acc[item.id] = {
                            ...item,
                            allTeis: [item.tei]
                        };
                    } else {
                        acc[item.id].allTeis.push(item.tei);
                    }
                    return acc;
                }, {});

            // Convert to array and use first TEI as primary
            const staffMembers = Object.values(matchedStaff).map(user => ({
                ...user,
                tei: user.allTeis[0]
            }));

            setStaffMembers(staffMembers);
            
        } catch (error) {
            console.error('Error fetching staff members:', error);
            setStaffError(error);
        } finally {
            setLoadingStaff(false);
        }
    };

    // Fetch staff members on component mount and after refetch
    useEffect(() => {
        let isMounted = true;

        const fetchStaffMembers = async () => {
            try {
                setLoadingStaff(true);
                setStaffError(null);
                
                // ... [your existing logic] ...

                if (isMounted) {
                    setStaffMembers(staffMembers);
                }
                
            } catch (error) {
                if (isMounted) {
                    setStaffError(error);
                }
            } finally {
                if (isMounted) {
                    setLoadingStaff(false);
                }
            }
        };

        fetchStaffMembers();

        return () => {
            isMounted = false;
        };
    }, []);
            

    // Refetch staff members when main data refetches
    useEffect(() => {
        if (data) {
            fetchStaffMembers();
        }
    }, [data]);

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // Mark the tab as initialized when it's first clicked
        if (!initializedTabs[tab]) {
            setInitializedTabs(prev => ({
                ...prev,
                [tab]: true
            }));
        }
    };

    // Check if we're still loading everything
    const isLoading = loading || additionalLoading || loadingStaff;
    
    if (isLoading) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <CircularLoader />
            <span style={{ marginLeft: '10px' }}>Loading staff data...</span>
          </div>
        );
    }
      
    if (error || additionalError || staffError) {
        return (
            <NoticeBox title="Error loading staff">
                {error?.message || additionalError?.message || staffError?.message}
            </NoticeBox>
        );
    }

    const currentUserOrgUnits = data?.me?.organisationUnits?.map(ou => ou.id) || []
    const allUsers = data?.users?.users || []

    const staffUsers = allUsers.filter(user => {
        const orgMatch = (user.organisationUnits || []).some(ou => currentUserOrgUnits.includes(ou.id))
        const isStaffRole = user.userCredentials?.userRoles?.some(role => role.name === 'Staff')
        return orgMatch && isStaffRole
    })

    const filteredStaff = staffMembers.filter((staff) => {
        const fullName = `${staff.firstName || ''} ${staff.surname || ''}`.toLowerCase()
        const username = staff.userCredentials?.username?.toLowerCase() || ''
        return (
            fullName.includes(search.toLowerCase()) ||
            username.includes(search.toLowerCase()) ||
            staff.email?.toLowerCase().includes(search.toLowerCase())
        )
    })

    const showNotification = (type, title, details) => {
        // Clear any existing timeout
        if (notificationTimeout.current) {
            clearTimeout(notificationTimeout.current);
        }
        
        setNotification({
            visible: true,
            type,
            title,
            details
        });

        notificationTimeout.current = setTimeout(() => {
            setNotification(prev => ({ ...prev, visible: false }));
        }, 5000);
    };

    const fetchUserByUsername = async (username) => {
        const response = await engine.query({
            users: {
                resource: 'users',
                params: {
                    filter: `username:eq:${username}`,
                    fields: 'id,username,firstName,surname,email,phoneNumber,department,userCredentials[userRoles[id]]',
                    paging: 1000
                }
            }
        });

        const users = response.users?.users || [];
        return users.length > 0 ? users[0] : null;
    };

   

    const handleCreate = async (teiPayload) => {
        setMode('create');
        const wrapper = { trackedEntityInstances: [teiPayload] };
        const result = await engine.mutate({
            resource: 'trackedEntityInstances',
            type: 'create',
            data: wrapper
        });
        console.log('Created TEI import summary:', result);
        setFormVisible(false);
        setSelectedStaff(null);
        refetch();
    }

   const fetchStaffForEdit = async (teiId) => {
    try {
        console.log('🔍 Fetching TEI data for:', teiId);
        
        // 1. Fetch TEI with ALL necessary fields
        const { trackedEntityInstance } = await engine.query({
            trackedEntityInstance: {
                resource: `trackedEntityInstances/${teiId}`,
                params: {
                    fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
                }
            }
        });

        const tei = trackedEntityInstance;
        
        // Extract attributes into a map
        const attrMap = {};
        tei.attributes?.forEach(attr => {
            attrMap[attr.attribute] = attr.value;
        });

        // Get username attribute
        const username = attrMap['g3RRIfDiQra'];
        
        // Get user ID attribute (if exists)
        const userId = attrMap['n8aAqQTVI8b'];
        
        // ✅ NEW: Get position ID if stored in TEI
        const positionId = attrMap['Qpg6rlKD7Wr'] || attrMap['positionId'] || '';
        
        // Get date of birth
        const dob = attrMap['XKkkELk8aPe'] || '';
        
        // Get org unit name
        let orgUnitName = '';
        if (tei.orgUnit) {
            try {
                const { organisationUnit } = await engine.query({
                    organisationUnit: {
                        resource: `organisationUnits/${tei.orgUnit}`,
                        params: { fields: 'id,name' }
                    }
                });
                orgUnitName = organisationUnit.name;
            } catch (ouError) {
                console.warn('⚠️ Could not fetch org unit name:', ouError);
            }
        }

        // ✅ IMPROVED: Try to fetch user data by userId FIRST, then by username
        let user = null;
        let userRole = '';
        let userDepartment = '';
        
        // First try: Fetch by userId if available
        if (userId) {
            try {
                const { user: userById } = await engine.query({
                    user: {
                        resource: `users/${userId}`,
                        params: {
                            fields: 'id,firstName,surname,email,phoneNumber,userCredentials[username,userRoles[id,name]],organisationUnits[id,name],attributeValues[attribute,value]'
                        }
                    }
                });
                user = userById;
                
                // Get user role
                userRole = user.userCredentials?.userRoles?.[0]?.id || '';
                
                // Get department from user attributeValues
                const departmentAttr = user.attributeValues?.find(attr => 
                    attr.attribute === 'HZG6mhL4FL5'
                );
                userDepartment = departmentAttr?.value || '';
                
                console.log(`✅ Found user by ID: ${userId} (${user.firstName} ${user.surname})`);
            } catch (idError) {
                console.warn(`⚠️ Could not fetch user by ID ${userId}:`, idError.message);
            }
        }
        
        // Second try: Fetch by username if userId didn't work
        if (!user && username) {
            try {
                const { users } = await engine.query({
                    users: {
                        resource: 'users',
                        params: {
                            filter: `userCredentials.username:eq:${username}`,
                            fields: 'id,firstName,surname,email,phoneNumber,userCredentials[username,userRoles[id,name]],organisationUnits[id,name],attributeValues[attribute,value]',
                            paging: false
                        }
                    }
                });

                user = users?.[0];
                
                if (user) {
                    // Get user role
                    userRole = user.userCredentials?.userRoles?.[0]?.id || '';
                    
                    // Get department from user attributeValues
                    const departmentAttr = user.attributeValues?.find(attr => 
                        attr.attribute === 'HZG6mhL4FL5'
                    );
                    userDepartment = departmentAttr?.value || '';
                    
                    console.log(`✅ Found user by username: ${username} (${user.firstName} ${user.surname})`);
                } else {
                    console.warn(`⚠️ User not found for username: ${username}`);
                }
            } catch (usernameError) {
                console.warn(`⚠️ User fetch by username failed:`, usernameError);
            }
        }

        // ✅ NEW: Fetch position details if positionId exists
        let positionDetails = null;
        if (positionId) {
            try {
                const { trackedEntityInstance: positionTei } = await engine.query({
                    trackedEntityInstance: {
                        resource: `trackedEntityInstances/${positionId}`,
                        params: {
                            fields: 'attributes[attribute,value]'
                        }
                    }
                });
                
                if (positionTei) {
                    const positionAttrMap = {};
                    positionTei.attributes?.forEach(attr => {
                        positionAttrMap[attr.attribute] = attr.value;
                    });
                    
                    positionDetails = {
                        id: positionId,
                        title: positionAttrMap['kSLgCjD5VtD'] || positionAttrMap['positionTitle'] || '',
                        code: positionAttrMap['qGpkmorEPsH'] || positionAttrMap['positionId'] || '',
                        orgUnit: positionAttrMap['orgUnit'] || ''
                    };
                    console.log(`✅ Found position: ${positionDetails.title} (${positionDetails.code})`);
                }
            } catch (positionError) {
                console.warn(`⚠️ Could not fetch position ${positionId}:`, positionError.message);
            }
        }

        // If no user found at all
        if (!user) {
            console.warn(`⚠️ No user found for TEI ${teiId}, returning TEI data only`);
            return {
                id: tei.trackedEntityInstance,
                userId: userId || null,
                orgUnit: tei.orgUnit,
                orgUnitName: orgUnitName,
                username: username || '',
                firstName: attrMap['firstName'] || attrMap['firsName'] || '',
                secondName: attrMap['surname'] || attrMap['secondName'] || '',
                email: attrMap['email'] || '',
                phoneNumber: attrMap['phoneNumber'] || '',
                gender: attrMap['HAOvVNYm8Lx'] || '',
                dob: dob,
                userRole: userRole || '',
                department: userDepartment || attrMap['department'] || '',
                positionId: positionId || '',
                positionDetails: positionDetails,
                organisationUnits: [],
                attributes: tei.attributes || [],
                missingUser: true
            };
        }

        // Return combined data
        return {
            id: tei.trackedEntityInstance,
            userId: user.id,
            orgUnit: tei.orgUnit,
            orgUnitName: orgUnitName,

            // Personal info from User Module
            firstName: user.firstName || '',
            secondName: user.surname || '',
            phoneNumber: user.phoneNumber || '',
            email: user.email || '',
            userRole: userRole || '',
            department: userDepartment || '',
            organisationUnits: user.organisationUnits || [],

            // Position info
            positionId: positionId || '',
            positionDetails: positionDetails,

            gender: attrMap['HAOvVNYm8Lx'] || '',
            dob: dob,

            username: user.userCredentials?.username || username || '',
            attributes: tei.attributes || [],
            missingUser: false
        };
    } catch (error) {
        console.error('❌ Error in fetchStaffForEdit:', error);
        // Return minimal data instead of throwing
        return {
            id: teiId,
            error: error.message,
            orgUnit: '',
            orgUnitName: '',
            username: '',
            attributes: [],
            fetchFailed: true
        };
    }
   };

    const handleEdit = async (staffData) => {
        try {
            console.log('📝 Parent: Edit requested for:', {
                userId: staffData.userId,
                username: staffData.username,
                hasTei: !!staffData.trackedEntityInstance,
                firstName: staffData.firstName,
                secondName: staffData.secondName
            });
            
            let detailedStaffData = { ...staffData };
            
            // If staff has TEI, fetch detailed data
            if (staffData.trackedEntityInstance && !staffData.missingTEI) {
                const fetchedData = await fetchStaffForEdit(staffData.trackedEntityInstance);
                
                console.log('📋 Fetched TEI data:', {
                    hasUser: !!fetchedData.userId,
                    firstName: fetchedData.firstName,
                    secondName: fetchedData.secondName,
                    userRole: fetchedData.userRole,
                    department: fetchedData.department,
                    positionId: fetchedData.positionId,
                    missingUser: fetchedData.missingUser
                });
                
                // ✅ SMART MERGE: Use fetched data but fall back to StaffList data
                detailedStaffData = {
                    // Start with StaffList data (has basic user info)
                    ...staffData,
                    // Override with fetched TEI data
                    ...fetchedData,
                    // CRITICAL: Ensure we keep userId from StaffList if fetched data doesn't have it
                    userId: fetchedData.userId || staffData.userId,
                    // CRITICAL: Ensure we keep username from StaffList if fetched data doesn't have it
                    username: fetchedData.username || staffData.username,
                    // CRITICAL: Use StaffList personal info if fetched data is empty
                    firstName: fetchedData.firstName || staffData.firstName || '',
                    secondName: fetchedData.secondName || staffData.secondName || '',
                    email: fetchedData.email || staffData.email || '',
                    phoneNumber: fetchedData.phoneNumber || staffData.phoneNumber || '',
                    // Use fetched role/department/position, fall back to StaffList
                    userRole: fetchedData.userRole || staffData.userRole || '',
                    department: fetchedData.department || staffData.department || '',
                    positionId: fetchedData.positionId || staffData.positionId || '',
                    positionDetails: fetchedData.positionDetails || staffData.positionDetails,
                    // Keep org unit from StaffList as fallback
                    orgUnit: fetchedData.orgUnit || staffData.orgUnit || staffData.assignedOu || '',
                    orgUnitName: fetchedData.orgUnitName || staffData.orgUnitName || staffData.assignedOuName || ''
                };
                
                if (fetchedData.missingUser || fetchedData.fetchFailed) {
                    console.log('⚠️ Using StaffList data due to TEI fetch issues');
                }
            } else {
                // For staff without TEI, enrich with user data
                console.log('👤 Editing staff without TEI:', staffData);
                
                if (staffData.userId) {
                    try {
                        const { user } = await engine.query({
                            user: {
                                resource: `users/${staffData.userId}`,
                                params: {
                                    fields: 'id,firstName,surname,email,phoneNumber,userCredentials[username,userRoles[id,name]],organisationUnits[id,name],attributeValues[attribute,value]'
                                }
                            }
                        });
                        
                        // Extract department from user attributeValues
                        const departmentAttr = user.attributeValues?.find(attr => 
                            attr.attribute === 'HZG6mhL4FL5'
                        );
                        
                        // Extract user role
                        const userRole = user.userCredentials?.userRoles?.[0]?.id || '';
                        
                        // Enhance the staffData with user information
                        detailedStaffData = {
                            ...staffData,
                            firstName: user.firstName || staffData.firstName || '',
                            secondName: user.surname || staffData.secondName || '',
                            email: user.email || '',
                            phoneNumber: user.phoneNumber || '',
                            userRole: userRole || staffData.userRole || '',
                            username: user.userCredentials?.username || staffData.username || '',
                            department: departmentAttr?.value || staffData.department || '',
                            // Get orgUnit from user data if missing
                            orgUnit: user.organisationUnits?.[0]?.id || staffData.orgUnit || '',
                            orgUnitName: user.organisationUnits?.[0]?.name || staffData.orgUnitName || '',
                            // Ensure organisationUnits array exists
                            organisationUnits: user.organisationUnits || staffData.organisationUnits || []
                        };
                        
                        console.log('✅ Enhanced staff data with user info');
                        
                    } catch (userFetchError) {
                        console.warn('⚠️ Could not fetch user details, using StaffList data:', userFetchError);
                    }
                }
            }
            
            // 🔧 FIX: Try to get position from active positions if not already set
            if (!detailedStaffData.positionId && detailedStaffData.userId) {
                console.log('🔍 Looking for active position for user:', detailedStaffData.userId);
                try {
                    // You might need to query positions to find one assigned to this user
                    // This depends on your position tracking system
                } catch (error) {
                    console.warn('⚠️ Could not fetch position info:', error);
                }
            }
            
            // 🔧 FIX: Ensure orgUnit/orgUnitName is never blank
            if (!detailedStaffData.orgUnit && detailedStaffData.assignedOu) {
                detailedStaffData.orgUnit = detailedStaffData.assignedOu;
            }
            if (!detailedStaffData.orgUnitName && detailedStaffData.assignedOuName) {
                detailedStaffData.orgUnitName = detailedStaffData.assignedOuName;
            }
            
            console.log('🔄 Final staff data for edit:', {
                id: detailedStaffData.id,
                userId: detailedStaffData.userId,
                username: detailedStaffData.username,
                firstName: detailedStaffData.firstName,
                secondName: detailedStaffData.secondName,
                email: detailedStaffData.email,
                phoneNumber: detailedStaffData.phoneNumber,
                userRole: detailedStaffData.userRole,
                department: detailedStaffData.department,
                positionId: detailedStaffData.positionId,
                orgUnit: detailedStaffData.orgUnit,
                orgUnitName: detailedStaffData.orgUnitName,
                gender: detailedStaffData.gender
            });
            
            setSelectedStaff(detailedStaffData);
            setMode('edit');
            setFormVisible(true);
            
        } catch (error) {
            console.error('❌ Failed in handleEdit:', error);
            // Fallback to basic data
            setSelectedStaff(staffData);
            setMode('edit');
            setFormVisible(true);
        }
    };

   
    const handleUpdate = async (payload) => {
    try {
        console.log('📝 Parent: Update requested for:', {
            userId: payload.userId,
            hasTEI: !payload.missingTEI,
            missingTEI: payload.missingTEI
        });

        if (payload.missingTEI) {
            console.log('👤 Updating user only (no TEI)');
            // Update user directly
            const userUpdateData = {
                firstName: payload.firstName,
                surname: payload.surname,
                email: payload.email,
                phoneNumber: payload.phoneNumber,
                userCredentials: {
                    username: payload.username,
                    userRoles: payload.userRole ? [{ id: payload.userRole }] : []
                },
                organisationUnits: payload.organisationUnits
            };

            await engine.mutate({
                resource: `users/${payload.userId}`,
                type: 'update',
                data: userUpdateData
            });

            showNotification('success', 'User updated', 'User information updated successfully');
        } else {
            console.log('✅ Updating user and TEI');
            await updateStaffComplete(engine, payload.userId, payload.id, payload);
            showNotification('success', 'Staff updated', 'User and TEI data updated successfully');
        }

        setFormVisible(false);
        setSelectedStaff(null);
        refetch();
        fetchStaffMembers(); // Refresh the staff list

    } catch (error) {
        console.error('Update failed:', error);
        showNotification('error', 'Update failed', error.message);
    }
};

    const handleDelete = async (teiId) => {
        if (window.confirm('Are you sure you want to delete this staff record?')) {
            await deleteMutate({ id: teiId })
            refetch()
        }
    }

    return (
        <div>
            <h3 style={{ color: 'green'}}>Staff Management</h3>

            {/* Tab Navigation */}
            <div style={{ marginBottom: '2rem' }}>
                <TabBar>
                    <Tab
                        selected={activeTab === 'staff'}
                        onClick={() => handleTabChange('staff')}
                    >
                        Staff Members
                    </Tab>
                    <Tab
                        selected={activeTab === 'positions'}
                        onClick={() => handleTabChange('positions')}
                    >
                        Position Management
                    </Tab>
     
                </TabBar>
            </div>

            {/* Notification */}
            {notification.visible && (
                <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 10000, width: '350px' }}>
                    <AlertBar
                        {...(notification.type === 'success' ? { success: true } : 
                            notification.type === 'error' ? { error: true } : 
                            { warning: true })}
                        title={notification.title}
                        onHidden={() => setNotification({ visible: false })}
                    >
                        {notification.details}
                    </AlertBar>
                </div>      
            )}

            {/* Staff Members Tab - Always rendered but visibility controlled by CSS */}
            <div style={{ display: activeTab === 'staff' ? 'block' : 'none' }}>
                {!formVisible && (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <button 
                            style={{
                            backgroundColor: '#063aa9ff',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                            cursor: 'pointer',
                            }}
                            onClick={() => {
                            setFormVisible(true);
                            setSelectedStaff(null);
                            setMode('create');
                        }}>
                            + Add Single Staff
                        </button>
                        
                        <Button onClick={() => setShowBulkImport(true)}>
                            📥 Import Staff in Bulk
                        </Button>
                    </div>
                )}

                {formVisible && (
                <StaffForm
                    initialData={selectedStaff}
                    onSubmit={selectedStaff ? handleUpdate : handleCreate}
                    onSuccess={() => {
                        setFormVisible(false);
                        setSelectedStaff(null);
                        refetch(); // Refresh the staff list
                    }}
                    onCancel={() => {
                        setFormVisible(false);
                        setSelectedStaff(null);
                    }}
                    mode={mode}
                />
                )}
                
                {/* Bulk Import Dialog */}
                <BulkStaffImportDialog
                    open={showBulkImport}
                    onClose={() => setShowBulkImport(false)}
                    onImportComplete={(results) => {
                        console.log('Import completed:', results);
                        // Refresh data after import
                        refetch();
                        fetchStaffMembers();
                        setShowBulkImport(false);
                    }}
                    departments={departments}
                    existingUsers={existingUsers}
                    positionsList={positionsList}
                />

                <StaffList 
                    onEdit={handleEdit} 
                    onDelete={handleDelete}  
                />
            </div>

            {/* Position Management Tab - Always rendered but visibility controlled by CSS */}
            <div style={{ display: activeTab === 'positions' ? 'block' : 'none' }}>
                {initializedTabs.positions && <PositionList key="position-list" />}
            </div>

            {/* Staff Assignments Tab - Always rendered but visibility controlled by CSS */}
            <div style={{ display: activeTab === 'assignments' ? 'block' : 'none' }}>
                {initializedTabs.assignments && <StaffPositionEnforcement key="assignments-list" />}
            </div>
        </div>
    )
}

export default StaffPage;